import React, { useState, useEffect } from 'react';
import api, { bulkCreateUsersAdmin } from '../services/api';
import { 
  PlusIcon, 
  XMarkIcon, 
  ArrowUpTrayIcon,
  UsersIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as XLSX from 'xlsx';

// Enhanced Modal Component
const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex justify-center items-center p-4">
      <div className="relative mx-auto w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-100">
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <h3 className="text-xl font-semibold text-gray-800">{title}</h3>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [newUser, setNewUser] = useState({
    namaLengkap: '',
    email: '',
    password: '',
    role: 'admin'
  });
  const [formError, setFormError] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editFormError, setEditFormError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // State for bulk upload
  const [isBulkUploadModalOpen, setIsBulkUploadModalOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
  const [bulkUploadSummary, setBulkUploadSummary] = useState(null);

  const handleBulkFileChange = (e) => {
    setBulkFile(e.target.files[0]);
    setBulkUploadSummary(null);
  };

  const handleBulkUserSubmit = async (e) => {
    e.preventDefault();
    if (!bulkFile) {
      toast.error('Please select a file to upload.');
      return;
    }

    setBulkUploadLoading(true);
    setBulkUploadSummary(null);
    const formData = new FormData();
    formData.append('userBulkFile', bulkFile);

    try {
      const response = await bulkCreateUsersAdmin(formData);
      toast.success(response.message || `${response.data?.successfullyCreated || 0} users processed.`);
      setBulkUploadSummary({
        status: response.status || (response.data?.errors?.length > 0 ? 'partial_success' : 'success'),
        message: response.message,
        data: response.data
      });
      fetchUsers();
    } catch (error) {
      console.error("Bulk upload error:", error);
      const errorMsg = error.response?.data?.message || error.message || 'Bulk user creation failed.';
      const errorData = error.response?.data?.data;
      toast.error(errorMsg);
      setBulkUploadSummary({
        status: 'fail',
        message: errorMsg,
        data: errorData
      });
    } finally {
      setBulkUploadLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/admin/users');
      if (response.data && response.data.status === 'success') {
        setUsers(response.data.data.users);
      } else {
        setError('Gagal mengambil data pengguna.');
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      setError(err.response?.data?.message || 'Terjadi kesalahan server. Coba lagi nanti.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleAddUserSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    
    try {
      const response = await api.post('/admin/users', newUser);
      if (response.data && response.data.status === 'success') {
        toast.success('User berhasil ditambahkan!');
        setIsAddUserModalOpen(false);
        setNewUser({ namaLengkap: '', email: '', password: '', role: 'admin' });
        fetchUsers();
      } else {
        setFormError(response.data.message || 'Gagal menambahkan user.');
      }
    } catch (err) {
      console.error("Error adding user:", err);
      setFormError(err.response?.data?.message || 'Terjadi kesalahan server. Coba lagi nanti.');
    }
  };

  const handleEditUserSubmit = async (e) => {
    e.preventDefault();
    setEditFormError('');
    
    try {
      const response = await api.put(`/admin/users/${editingUser.id}`, editingUser);
      if (response.data && response.data.status === 'success') {
        toast.success('User berhasil diperbarui!');
        setIsEditModalOpen(false);
        setEditingUser(null);
        fetchUsers();
      } else {
        setEditFormError(response.data.message || 'Gagal memperbarui user.');
      }
    } catch (err) {
      console.error("Error updating user:", err);
      setEditFormError(err.response?.data?.message || 'Terjadi kesalahan server. Coba lagi nanti.');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus user ini?')) {
      return;
    }
    
    try {
      const response = await api.delete(`/admin/users/${userId}`);
      if (response.data && response.data.status === 'success') {
        toast.success('User berhasil dihapus!');
        fetchUsers();
      } else {
        toast.error(response.data.message || 'Gagal menghapus user.');
      }
    } catch (err) {
      console.error("Error deleting user:", err);
      toast.error(err.response?.data?.message || 'Terjadi kesalahan server. Coba lagi nanti.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser(prev => ({ ...prev, [name]: value }));
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditingUser(prev => ({ ...prev, [name]: value }));
  };

  const openEditModal = (user) => {
    setEditingUser({ ...user, password: '' });
    setIsEditModalOpen(true);
  };

  const filteredUsers = users.filter(user =>
    user.namaLengkap?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.role?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Memuat data pengguna...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-red-800 mb-2">Terjadi Kesalahan</h3>
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border border-gray-100">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="mb-4 md:mb-0">
                <div className="flex items-center mb-2">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mr-3">
                    <UsersIcon className="w-5 h-5 text-white" />
                  </div>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-800">User Management</h1>
                </div>
                <p className="text-gray-600">Kelola pengguna sistem TERAPLUS</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setIsBulkUploadModalOpen(true)}
                  className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-medium rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  <ArrowUpTrayIcon className="w-4 h-4 mr-2" />
                  Bulk Upload
                </button>
                <button
                  onClick={() => setIsAddUserModalOpen(true)}
                  className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium rounded-xl hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Add User
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Cari pengguna berdasarkan nama, email, atau role..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
            />
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-800">Daftar Pengguna ({filteredUsers.length})</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors duration-200">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                          {user.namaLengkap?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.namaLengkap}</div>
                          <div className="text-sm text-gray-500">ID: {user.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.role === 'admin' 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircleIcon className="w-3 h-3 mr-1" />
                        Active
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => openEditModal(user)}
                          className="text-blue-600 hover:text-blue-900 p-2 rounded-lg hover:bg-blue-50 transition-colors duration-200"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="text-red-600 hover:text-red-900 p-2 rounded-lg hover:bg-red-50 transition-colors duration-200"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <UsersIcon className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Tidak Ada Pengguna</h3>
              <p className="text-gray-600">
                {searchTerm ? 'Tidak ada pengguna yang cocok dengan pencarian Anda.' : 'Belum ada pengguna terdaftar.'}
              </p>
            </div>
          )}
        </div>

        {/* Add User Modal */}
        <Modal isOpen={isAddUserModalOpen} onClose={() => setIsAddUserModalOpen(false)} title="Add New User">
          <form onSubmit={handleAddUserSubmit} className="space-y-4">
            {formError && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                <div className="flex items-center">
                  <XCircleIcon className="w-5 h-5 text-red-600 mr-2" />
                  <span className="text-red-700 text-sm">{formError}</span>
                </div>
              </div>
            )}
            
            <div>
              <label htmlFor="namaLengkap" className="block text-sm font-medium text-gray-700 mb-1">Nama Lengkap</label>
              <input
                type="text"
                name="namaLengkap"
                id="namaLengkap"
                value={newUser.namaLengkap}
                onChange={handleInputChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                required
              />
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                name="email"
                id="email"
                value={newUser.email}
                onChange={handleInputChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                required
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                name="password"
                id="password"
                value={newUser.password}
                onChange={handleInputChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                required
              />
            </div>
            
            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select
                name="role"
                id="role"
                value={newUser.role}
                onChange={handleInputChange}
                className="block w-full px-3 py-2 border border-gray-300 bg-white rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="admin">Admin</option>
                <option value="user">User</option>
              </select>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setIsAddUserModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                Add User
              </button>
            </div>
          </form>
        </Modal>

        {/* Edit User Modal */}
        {editingUser && (
          <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title={`Edit User: ${editingUser.namaLengkap}`}>
            <form onSubmit={handleEditUserSubmit} className="space-y-4">
              {editFormError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                  <div className="flex items-center">
                    <XCircleIcon className="w-5 h-5 text-red-600 mr-2" />
                    <span className="text-red-700 text-sm">{editFormError}</span>
                  </div>
                </div>
              )}
              
              <div>
                <label htmlFor="editNamaLengkap" className="block text-sm font-medium text-gray-700 mb-1">Nama Lengkap</label>
                <input
                  type="text"
                  name="namaLengkap"
                  id="editNamaLengkap"
                  value={editingUser.namaLengkap}
                  onChange={handleEditInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="editEmail" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  id="editEmail"
                  value={editingUser.email}
                  onChange={handleEditInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="editPassword" className="block text-sm font-medium text-gray-700 mb-1">Password (leave blank to keep current)</label>
                <input
                  type="password"
                  name="password"
                  id="editPassword"
                  value={editingUser.password || ''}
                  onChange={handleEditInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
              
              <div>
                <label htmlFor="editRole" className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  name="role"
                  id="editRole"
                  value={editingUser.role}
                  onChange={handleEditInputChange}
                  className="block w-full px-3 py-2 border border-gray-300 bg-white rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="admin">Admin</option>
                  <option value="user">User</option>
                </select>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Save Changes
                </button>
              </div>
            </form>
          </Modal>
        )}

        {/* Bulk User Upload Modal */}
        <Modal isOpen={isBulkUploadModalOpen} onClose={() => setIsBulkUploadModalOpen(false)} title="Bulk Create Users via Excel">
          <form onSubmit={handleBulkUserSubmit} className="space-y-4">
            <div>
              <label htmlFor="bulkUserFile" className="block text-sm font-medium text-gray-700 mb-2">
                Upload Excel File (.xlsx, .xls, .csv)
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Required columns: "namaLengkap", "email", "password", "role". Optional: "affiliasi", "noHp".
              </p>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors duration-200">
                <input
                  type="file"
                  name="bulkUserFile"
                  id="bulkUserFile"
                  accept=".xlsx, .xls, .csv"
                  onChange={handleBulkFileChange}
                  required
                  className="hidden"
                />
                <label htmlFor="bulkUserFile" className="cursor-pointer">
                  <ArrowUpTrayIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">
                    {bulkFile ? bulkFile.name : 'Click to select file or drag and drop'}
                  </p>
                </label>
              </div>
            </div>

            {bulkUploadSummary && (
              <div className={`p-4 rounded-xl text-sm ${
                bulkUploadSummary.status === 'success' ? 'bg-green-50 text-green-700 border border-green-200' :
                bulkUploadSummary.status === 'partial_success' ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' :
                'bg-red-50 text-red-700 border border-red-200'
              }`}>
                <div className="flex items-center mb-2">
                  {bulkUploadSummary.status === 'success' ? (
                    <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
                  ) : bulkUploadSummary.status === 'partial_success' ? (
                    <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 mr-2" />
                  ) : (
                    <XCircleIcon className="w-5 h-5 text-red-600 mr-2" />
                  )}
                  <p className="font-semibold">{bulkUploadSummary.message}</p>
                </div>
                {bulkUploadSummary.data && (
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    {typeof bulkUploadSummary.data.totalUsersInFile === 'number' && 
                      <li>Total users in file: {bulkUploadSummary.data.totalUsersInFile}</li>
                    }
                    {typeof bulkUploadSummary.data.successfullyCreated === 'number' && 
                      <li>Successfully created: {bulkUploadSummary.data.successfullyCreated}</li>
                    }
                    {typeof bulkUploadSummary.data.skippedOrFailed === 'number' && 
                      <li>Skipped/Failed: {bulkUploadSummary.data.skippedOrFailed}</li>
                    }
                    {bulkUploadSummary.data.errors && bulkUploadSummary.data.errors.length > 0 && (
                      <li>
                        Errors:
                        <ul className="list-disc list-inside pl-5 max-h-32 overflow-y-auto mt-1">
                          {bulkUploadSummary.data.errors.map((err, idx) => (
                            <li key={idx}>{err.email || `Row ${err.row || idx + 2}`}: {err.message}</li>
                          ))}
                        </ul>
                      </li>
                    )}
                  </ul>
                )}
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setIsBulkUploadModalOpen(false);
                  setBulkFile(null);
                  setBulkUploadSummary(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors duration-200"
                disabled={bulkUploadLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50"
                disabled={!bulkFile || bulkUploadLoading}
              >
                {bulkUploadLoading ? 'Uploading...' : 'Upload and Create Users'}
              </button>
            </div>
          </form>
        </Modal>
      </div>
    </div>
  );
};

export default AdminUsersPage;
